import { getUID } from '@redviking/argonaut-util/src/getUID';
import { accessor } from '@redviking/argonaut-core-ui/src/store';
import type { FisDataLabelFragment } from 'types/db';
import { FisDataTimingEnum } from 'types/db';
import { createNodeSocket } from '../../sync-utils/create-node-socket';
import type { UiNodeSpec } from './UiNodeSpec';
import type { NodeCfg } from '@redviking/argonaut-util/types/mes/applet-designs/flow.latest';
import { performanceFeatureFlags } from '@redviking/argonaut-util/src/feature-flags';

const CORE_STATE_DATA_LABEL_ID = '74127943-ce8a-47c0-89fd-c65d174072f2';

export const performanceDataCollectUiNodeSpec: UiNodeSpec<'performanceDataCollect'> = {
    requiredSockets: () => ({
            conditionallyRequired: [],
            required: [ 'trigger', 'numericValue' ],
    }),
    cfg: () => {
        const ret: NodeCfg<'performanceDataCollect'> = {
            disabled: false,
            controlValues: {
                dataLabelId: CORE_STATE_DATA_LABEL_ID,
                timing: FisDataTimingEnum.Start,
                assetCfg: {
                    id: '',
                },
            },
            id: getUID(),
            kind: 'performanceDataCollect',
        };
        return ret;
    },
    controls: getNodeCfg => ({
        assetCfg: {
            nodeCfg: getNodeCfg(),
            value: getNodeCfg().controlValues.assetCfg,
            cfg: {
                kind: 'asset',
                label: 'Asset',
            },
        },
        dataLabelId: {
            cfg: {
                kind: 'performanceDataCollect.dataLabelId',
                label: 'Data Label',
                onSelect: (dataLabel: FisDataLabelFragment) => {
                    const nodeCfg = getNodeCfg();
                    const controlValues: NodeCfg<'performanceDataCollect'>['controlValues'] = {
                        ...nodeCfg.controlValues,
                        dataLabelId: dataLabel.id,
                    };
                    const newNodeCfg = {
                        ...nodeCfg,
                        controlValues,
                    };
                    createNodeSocket({
                        newNodeCfg,
                    });
                },
            },
            nodeCfg: getNodeCfg(),
            value: getNodeCfg().controlValues.dataLabelId,
        },
    }),
    inputs: nodeCfg => ({
        trigger: { socket: 'event', label: 'Trigger' },
        numericValue: {
            control: {
                cfg: { kind: 'number', nullable: true },
                nodeCfg,
                nullable: true,
                value: nodeCfg.socketDefaults?.inputs?.numericValue,
            },
            socket: 'number',
            label: 'Numeric Value',
        },
    }),
    kindLabel: 'Performance Data Collect',
    nodeCategory: 'argonautApi',
    outputs: () => ({
        next: { socket: 'event', label: 'Next' },
    }),
    suppressNode: () => !accessor.testFeatureFlagRequirements([ { or: performanceFeatureFlags } ]),
    validator: node => {
        const errors: Record<string, string> = {};
        if (!node.controlValues.dataLabelId) {
            errors.dataLabelId = 'Data Label is required';
        }
        if (!node.controlValues.assetCfg) {
            errors.assetCfg = 'Asset is required';
        } else if ('id' in node.controlValues.assetCfg) {
            if (!node.controlValues.assetCfg.id) {
                errors.assetCfg = 'Asset is required';
            }
        } else if (!node.controlValues.assetCfg.deployParamId) {
            errors.assetCfg = 'Asset is required';
        }
        return Object.keys(errors).length ? errors : true;
    },
};
